import type { PinnedNotice } from '../lib/api.ts';
import { Attachments } from './Attachments.tsx';

interface Props {
  readonly notices: readonly PinnedNotice[];
  readonly onOpen: (id: string) => void;
  /** Only a device confirms reading, and only a device is
      offered the control. */
  readonly canRead?: boolean;
}

/**
 * The primary surface.
 *
 * A reverse-chronological feed buries the morning's price change by
 * lunchtime. These stay above the conversation until they expire or are
 * acknowledged -- which is the entire reason this is an operations
 * board and not a chat app.
 *
 * Shown across all categories regardless of the selected tab: what is
 * pinned for this site matters wherever the user happens to be looking.
 */
export const PinnedBoard = (
  { notices, onOpen, canRead }: Props,
) => {
  if (notices.length === 0) return null;

  return (
    <section className="pinned" aria-label="Pinned notices">
      {/* Pinned notices deliberately show in every category, so an
          urgent one is not missed because staff were on another tab.
          Without a heading that reads as a message leaking in from
          somewhere it does not belong. */}
      <p className="pinHead mono">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M9 4 h6 l-1 5 4 3.5 v2 H6 v-2 l4-3.5 z" />
          <path d="M12 14.5 V20" />
        </svg>
        Pinned &mdash; shown in every category
      </p>

      {notices.map((n) => (
        <article
          key={n.id}
          className={n.priority === 'urgent' ? 'pin urgent' : 'pin'}
          style={{ borderLeftColor: n.color }}
        >
          <div className="pinTop">
            {/* "from Promo" not "Promo": this card is showing inside
                whichever category you are viewing, so the chip has to
                read as origin rather than placement. */}
            <span className="pinCat" style={{ color: n.color }}>
              from {n.category}
            </span>
            {n.priority === 'urgent' && <span className="pinUrg">Urgent</span>}
            {n.requires_ack && !n.acked_at && (
              <span className="pinAck">Acknowledgement required</span>
            )}
            {n.acked_by && (
              <span className="pinDone">Acknowledged by {n.acked_by}</span>
            )}
          </div>

          <p className="pinBody">{n.body}</p>

          {/* A notice pinned with a photo of the thing it is
              about is the common case, and the pinned board
              was the one place that never showed it. */}
          <Attachments messageId={n.id} />

          <div className="pinFoot">
            <span className="mono">{n.sender ?? 'System'}</span>
            {/* Reading is what a site does. An account
                confirming it read its own notice is not
                evidence the site saw it, and the server
                refuses the receipt anyway. */}
            {!canRead ? null : n.opened_at ? (
              <span className="mono pinSeen">Seen</span>
            ) : (
              <button className="primary" onClick={() => onOpen(n.id)}>
                I have read this
              </button>
            )}
          </div>
        </article>
      ))}
    </section>
  );
};
